import { getCookie, removeCookie, setCookie } from '@/app/common/cookies/clientCookies';
import { COOKIE_KEY } from '@/app/common/cookies/types';

/**
 * MPH-1291: the `tsx_vh` cookie, the last hotels a user opened in the Evo hotel detail. tsx-ui only writes it;
 * reading it and showing "recently viewed hotels" belongs to the consumer (the home page).
 *
 * Contract for the consumer:
 * - Name `tsx_vh`; domain `.<brand>` (as the rest of the TSX cookies), `path=/`, not httpOnly, 30 days renewed on every write.
 * - Value `<version>.<id1>.<id2>…`, version `1`, ids are positive integers (the `internalHotelId`), at most 10,
 *   most recent first, no repeats. Example: `1.141036.139497.98123`. Nothing else is stored (no dates, pax, codes).
 * - Read tolerantly: another version, empty or malformed value means an empty list; segments that are not
 *   positive integers of 1-10 digits are dropped; repeats keep the first; trim to 10.
 * - The cookie can be missing: toggle off, skipped by the size guard when not even the newest id fits (and deleted), or expired. That is a normal state.
 * - A change of format bumps the version (`2.`).
 */
export const VIEWED_HOTELS_LIMIT = 10;
export const VIEWED_HOTELS_FORMAT_VERSION = '1';
export const VIEWED_HOTELS_SEPARATOR = '.';
export const VIEWED_HOTELS_EXPIRE_DAYS = 30;

/**
 * Most bytes the cookies visible to `document.cookie` may weigh once `tsx_vh` is written. Budget =
 * (L - H - P - M) rounded down to 500, where
 * L = 14_000: header limit of the Apache in front of TSX;
 * H = 1_045: httpOnly cookies with path=/ (cf_clearance, __cf_bm, _cfuvid, __csrf_nonce), measured in prod;
 * P = 655: cookies with path=/s/tsx that only travel to TSX and are invisible here (max observed);
 * M = 1_700: margin for cookies that other products add after our write.
 * 14_000 - 1_045 - 655 - 1_700 = 10_600 -> 10_500.
 */
export const VIEWED_HOTELS_VISIBLE_COOKIE_BUDGET_BYTES = 10_500;

const MAX_ID_DIGITS = 10;
const ID_PATTERN = new RegExp(`^[1-9][0-9]{0,${MAX_ID_DIGITS - 1}}$`);

const isViewedHotelId = (id: number) => Number.isInteger(id) && id > 0;

export const serialiseViewedHotels = (ids: number[]): string =>
  [VIEWED_HOTELS_FORMAT_VERSION, ...ids].join(VIEWED_HOTELS_SEPARATOR);

export const parseViewedHotels = (raw: string | undefined): number[] => {
  if (!raw) return [];
  const [version, ...segments] = raw.split(VIEWED_HOTELS_SEPARATOR);
  if (version !== VIEWED_HOTELS_FORMAT_VERSION) return [];
  const ids = segments.filter((segment) => ID_PATTERN.test(segment)).map(Number);
  return ids.filter((id, index) => ids.indexOf(id) === index).slice(0, VIEWED_HOTELS_LIMIT);
};

export const pushViewedHotel = (ids: number[], id: number): number[] => {
  if (!isViewedHotelId(id)) return ids;
  return [id, ...ids.filter((existing) => existing !== id)].slice(0, VIEWED_HOTELS_LIMIT);
};

// Bytes the browser sends for a string: its UTF-8 length, counted without Buffer, TextEncoder or Blob.
export const utf8Bytes = (value: string): number =>
  Array.from(value).reduce((bytes, char) => {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x80) return bytes + 1;
    if (code < 0x800) return bytes + 2;
    return bytes + (code < 0x10000 ? 3 : 4);
  }, 0);

/** Bytes that the `tsx_vh` entry of `value` (as `setCookie` encodes it) adds to the Cookie header; 0 for no cookie. */
export const viewedHotelsEntryBytes = (value: string | undefined): number =>
  value === undefined ? 0 : utf8Bytes(`; ${COOKIE_KEY.VIEWED_HOTELS}=${encodeURIComponent(value)}`);

export const exceedsVisibleCookieBudget = (
  visibleCookies: string,
  currentValue: string | undefined,
  newValue: string
): boolean =>
  utf8Bytes(visibleCookies) - viewedHotelsEntryBytes(currentValue) + viewedHotelsEntryBytes(newValue) >
  VIEWED_HOTELS_VISIBLE_COOKIE_BUDGET_BYTES;

/**
 * The longest prefix of `ids` (newest first) whose cookie fits the budget, so the oldest ids go first; `undefined` when
 * not even the newest id alone fits.
 */
export const fitViewedHotelsToBudget = (
  visibleCookies: string,
  currentValue: string | undefined,
  ids: number[]
): number[] | undefined =>
  Array.from({ length: ids.length }, (_, index) => ids.slice(0, ids.length - index)).find(
    (candidate) => !exceedsVisibleCookieBudget(visibleCookies, currentValue, serialiseViewedHotels(candidate))
  );

export const readViewedHotelsCookie = (): number[] => {
  try {
    return parseViewedHotels(getCookie(COOKIE_KEY.VIEWED_HOTELS));
  } catch {
    return [];
  }
};

export const writeViewedHotelsCookie = (ids: number[]) => {
  setCookie(COOKIE_KEY.VIEWED_HOTELS, serialiseViewedHotels(ids), VIEWED_HOTELS_EXPIRE_DAYS);
};

export const removeViewedHotelsCookie = () => {
  removeCookie(COOKIE_KEY.VIEWED_HOTELS);
};
